import React, {useEffect, useState} from 'react';

import {useLazyQuery, gql, ApolloError} from '@apollo/client';
import {Grid, Box, Button} from '@mui/material';
import {GridColDef} from '@mui/x-data-grid-pro';
import {useNavigate} from 'react-router-dom';
import {
  SearchIdentityUsers,
  SearchIdentityUsersVariables,
} from '../../schema-types/SearchIdentityUsers';
import {CreateIdentityUser as CreateIdentityUserModal} from './CreateIdentityUser';
import {ErrorPage} from '../ErrorPage';
import {useDebounce} from '../../hooks/useDebounce';
import withAutoFormNew from '../../components/AutoForm/AutoFormNew';
import {SearchInput} from '../AccountContactSearchPage/SearchInput';
import {themeColors} from '../../theme/Theme';
import {SearchResults} from '../../components/Common/SearchResults';
import {
  ColoredChip,
  RenderColor,
  RenderColorBasic,
} from '../../components/EditorGrid/GridRenderComponents';
import {contactTypeColors} from '../../components/ContactPage/contactStyles';
import {CognitoUserType} from '../../graphql/scalars';
import {COGNITO_USER_FRAGMENT} from '../../graphql/fragments';

export const SEARCH_IDENTITY_USERS_QUERY = gql`
  ${COGNITO_USER_FRAGMENT}
  query SearchIdentityUsers($searchTerm: String!) {
    searchIdentityUsers(searchTerm: $searchTerm) {
      users {
        ...CognitoUserFragment
      }
      ok
      errors
    }
  }
`;

const identityUserColumns: GridColDef<CognitoUserType>[] = [
  {
    field: 'subId',
    headerName: 'Username (sub id)',
    flex: 2,
  },
  {
    field: 'email',
    headerName: 'Email',
    flex: 2,
  },
  {
    field: 'emailVerified',
    headerName: 'Email Verified',
    flex: 0.7,

    renderCell: ({value}) => {
      const text = value ? 'verified' : 'not verified';
      return (
        <ColoredChip
          colorScheme={{
            verified: themeColors.lightGreen,
            'not verified': themeColors.lightAlert,
          }}
          value={text}
        />
      );
    },
  },

  {
    field: 'status',
    headerName: 'Status',
    flex: 0.7,
    align: 'center',
    valueGetter: (value, row) => row.status || '',
    renderCell: RenderColor(contactTypeColors),
  },
  {
    field: 'enabled',
    headerName: 'Enabled',
    flex: 0.5,
    type: 'boolean',
  },
  {
    field: 'mfaEnabled',
    headerName: 'MFA',
    flex: 0.5,
    type: 'boolean',
  },
  {
    field: 'mfaSetting',
    headerName: 'MFA Setting',
    flex: 0.7,
    renderCell: RenderColor({
      Inactive: themeColors.lightWarning,
      Active: themeColors.lightGreen,
    }),
  },
  {
    field: 'lastModifiedAt',
    headerName: 'Last Modified',
    flex: 1,
    type: 'dateTime',
    valueGetter: (value, row) => row.lastModifiedAt && new Date(row.lastModifiedAt),
  },
];

const IdentityUserSearchInner: React.FC = () => {
  const [search, setSearch] = useState<string>('');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const delayedSearch = useDebounce(search, 1000);
  const [getIdentityUsers, {loading, error, data}] = useLazyQuery<
    SearchIdentityUsers,
    SearchIdentityUsersVariables
  >(SEARCH_IDENTITY_USERS_QUERY);

  useEffect(() => {
    if (delayedSearch.length > 3) {
      getIdentityUsers({
        variables: {
          searchTerm: delayedSearch,
        },
      });
    }
  }, [delayedSearch]);

  return (
    <>
      <Grid
        container
        paddingRight={3}
        mb={3}
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
      >
        <Grid item flex={1}>
          <Box bgcolor={themeColors.white} borderRadius="4px">
            <SearchInput setSearch={setSearch} search={search} />
          </Box>
        </Grid>
        <Grid item>
          <Button variant="contained" onClick={() => setCreateModalOpen(true)}>
            Create New Identity User
          </Button>
        </Grid>
      </Grid>
      <IdentitySearchResults
        data={data}
        loading={loading}
        error={error}
        search={search}
      />
      <CreateIdentityUserModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={() => {
          setCreateModalOpen(false);
          if (delayedSearch.length > 3) {
            getIdentityUsers({
              variables: {
                searchTerm: delayedSearch,
              },
            });
          }
        }}
      />
    </>
  );
};

const IdentitySearchResults: React.FC<{
  data?: SearchIdentityUsers;
  loading: boolean;
  error?: ApolloError;
  search: string;
}> = ({data, loading, error, search}) => {
  const navigate = useNavigate();
  if (error) {
    return <ErrorPage error={error} text="Unable to load identity users" />;
  }
  if (data?.searchIdentityUsers?.errors?.length) {
    return (
      <ErrorPage
        error={
          new Error(
            data?.searchIdentityUsers?.errors[0] || 'Unable to load identity users'
          )
        }
        text="Unable to load identity users"
      />
    );
  }
  return (
    <SearchResults
      dataGridId="IdentitySearch"
      title="Identity"
      placeholder="Search by email or sub id"
      entity="Identity"
      loading={loading}
      search={search}
      columns={identityUserColumns}
      data={data?.searchIdentityUsers?.users || []}
      onRowDoubleClick={(row) => {
        navigate(`/identity/${row.id}`);
      }}
    />
  );
};

type IdentityUserSearchType = {
  search: string;
};

const IdentityUserSearchWrapped = withAutoFormNew<IdentityUserSearchType, object>(
  IdentityUserSearchInner,
  undefined,
  undefined
);

export const IdentityUserSearch: React.FC = () => {
  return (
    <>
      <IdentityUserSearchWrapped formFields={[]} />
    </>
  );
};
